import SkeletonBlock from 'components/SkeletonBlock';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import InputExternalPropertyNotTag from 'pages/accountDrawer/externalPropertyNotTag/inputExternalPropertyNotTag';

interface MeetingExternalPropertiesProps {
  isLoading: boolean;
  externalProperties: ExternalProperty[];
  values: Record<number, string>;
  onChange: (externalPropertyId: number, value: string) => void;
}

/**
 * The CRM fields the meeting is logged with. The widget for each value type —
 * and the mapping from the options sent to the CRM to the labels shown here —
 * comes from `InputExternalPropertyNotTag`.
 */
export function MeetingExternalProperties({
  isLoading,
  externalProperties,
  values,
  onChange,
}: MeetingExternalPropertiesProps) {
  // How many fields are coming is only known once the response lands, so a
  // single row stands for the section and keeps the footer from jumping.
  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading the CRM fields"
        className="flex flex-col gap-1"
      >
        <SkeletonBlock className="h-4 w-16" />
        <SkeletonBlock className="h-8 w-full" />
      </div>
    );
  }

  return (
    <>
      {externalProperties.map((externalProperty) => (
        <label
          key={externalProperty.id}
          className="flex flex-col gap-1 text-sm font-semibold text-gray-900"
        >
          <span>
            {externalProperty.publicName}
            {externalProperty.isMandatory && '*'}
          </span>

          <InputExternalPropertyNotTag
            externalProperty={externalProperty}
            externalValue={{
              externalPropertyId: externalProperty.id,
              value: values[externalProperty.id] ?? '',
            }}
            owned
            // The meeting doesn't exist yet, so every syncable field is fillable
            // regardless of whether the CRM lets it be edited afterwards.
            bypassDisabled
            showLabel={false}
            handleSetValue={onChange}
          />
        </label>
      ))}
    </>
  );
}
